import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useCreateVisitor, useVisitorByDocument } from "@/hooks/useVisitors"
import { useUnits } from "@/hooks/useUnits"
import { useMotoboys } from "@/hooks/useMotoboys"
import { useAuth } from "@/hooks/useAuth"
import { Plus, Loader2, Check } from "lucide-react"

interface RegistrarVisitanteModalProps {
  children?: React.ReactNode
}

const RegistrarVisitanteModal = ({ children }: RegistrarVisitanteModalProps) => {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    document: "",
    unit_id: "",
    visitor_type: "guest" as "guest" | "delivery" | "service" | "other",
    vehicle_plate: "",
    notes: "",
    authorized_by: null as string | null
  })

  const { data: units } = useUnits()
  const { data: motoboys } = useMotoboys()
  const { mutate: createVisitor, isPending } = useCreateVisitor()
  const { profile } = useAuth()
  const { data: previousVisitor, isLoading: isSearchingVisitor } = useVisitorByDocument(
    formData.document,
    profile?.condominium_id || null
  )

  // Função para buscar motoboy pelo nome
  const findMotoboyByName = (name: string) => {
    if (!motoboys || name.length < 3) return null
    return motoboys.find(motoboy => 
      motoboy.name.toLowerCase().includes(name.toLowerCase())
    )
  }

  // Effect para preencher dados automaticamente quando encontrar visitante por documento
  useEffect(() => {
    if (previousVisitor && formData.document && !formData.name) {
      setFormData(prev => ({
        ...prev,
        name: previousVisitor.name,
        visitor_type: (previousVisitor.visitor_type || "guest") as "guest" | "delivery" | "service" | "other",
        vehicle_plate: previousVisitor.vehicle_plate || "",
        unit_id: previousVisitor.unit_id || "",
        notes: `Visitante recorrente - Última visita: ${new Date(previousVisitor.created_at).toLocaleDateString()}`
      }))
    }
  }, [previousVisitor, formData.document, formData.name])

  // Effect para preencher dados automaticamente quando digitar nome de motoboy
  useEffect(() => {
    if (formData.visitor_type === "delivery" && formData.name && !previousVisitor) {
      const foundMotoboy = findMotoboyByName(formData.name)
      if (foundMotoboy) {
        setFormData(prev => ({
          ...prev,
          document: foundMotoboy.document || "",
          vehicle_plate: foundMotoboy.vehicle_info || "",
          notes: `Motoboy cadastrado: ${foundMotoboy.name}`
        }))
      }
    }
  }, [formData.name, formData.visitor_type, motoboys, previousVisitor])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const visitorData = {
      ...formData,
      entry_time: new Date().toISOString(),
      visit_date: new Date().toISOString(),
      status: "approved" as const,
      authorized_by: formData.authorized_by || null,
      unit_id: formData.unit_id || null,
      photo_url: null,
      exit_time: null
    }

    createVisitor(visitorData, {
      onSuccess: () => {
        setOpen(false)
        setFormData({
          name: "",
          document: "",
          unit_id: "",
          visitor_type: "guest",
          vehicle_plate: "",
          notes: "",
          authorized_by: null
        })
      }
    })
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const isMotoboyType = formData.visitor_type === "delivery"

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Registrar Entrada
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Entrada de Visitante</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="visitor_type">Tipo de Visitante</Label>
              <Select value={formData.visitor_type} onValueChange={(value) => handleInputChange("visitor_type", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guest">Visitante</SelectItem>
                  <SelectItem value="delivery">Motoboy/Entregador</SelectItem>
                  <SelectItem value="service">Prestador de Serviço</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder={isMotoboyType ? "Digite o nome do motoboy" : "Nome completo do visitante"}
                required
              />
              {isMotoboyType && formData.name.length >= 3 && (
                <p className="text-xs text-gray-500 mt-1">
                  {findMotoboyByName(formData.name) 
                    ? "✓ Motoboy encontrado - dados preenchidos automaticamente" 
                    : "Novo motoboy - preencha os dados"}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="document">
                Documento/CPF {isMotoboyType ? "*" : ""}
                {isSearchingVisitor && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    <Loader2 className="inline h-3 w-3 animate-spin" /> Buscando...
                  </span>
                )}
              </Label>
              <Input
                id="document"
                value={formData.document}
                onChange={(e) => handleInputChange("document", e.target.value)}
                placeholder="CPF, RG ou CNPJ"
                required={isMotoboyType}
              />
              {previousVisitor && formData.document.length >= 5 && (
                <p className="text-xs text-green-600 mt-1 flex items-center">
                  <Check className="h-3 w-3 mr-1" />
                  Visitante encontrado: {previousVisitor.name} - Dados preenchidos automaticamente
                </p>
              )}
              {!previousVisitor && formData.document.length >= 5 && !isSearchingVisitor && (
                <p className="text-xs text-muted-foreground mt-1">
                  Novo visitante - preencha os dados
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="vehicle_plate">Placa do Veículo {isMotoboyType ? "*" : ""}</Label>
              <Input
                id="vehicle_plate"
                value={formData.vehicle_plate}
                onChange={(e) => handleInputChange("vehicle_plate", e.target.value)}
                placeholder="ABC-1234"
                required={isMotoboyType}
              />
            </div>

            <div>
              <Label htmlFor="unit_id">{isMotoboyType ? "Casa/Unidade de Destino *" : "Unidade de Destino"}</Label>
              <Select value={formData.unit_id} onValueChange={(value) => handleInputChange("unit_id", value)}>
                <SelectTrigger>
                  <SelectValue placeholder={isMotoboyType ? "Selecione a casa" : "Selecione a unidade"} />
                </SelectTrigger>
                <SelectContent>
                  {units?.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.building ? `${unit.building} - ` : ""}Unidade {unit.number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isMotoboyType && (
                <p className="text-xs text-gray-500 mt-1">
                  O motoboy pode visitar múltiplas casas. Registre uma entrada para cada casa.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder={isMotoboyType ? "Ex: Entrega para apartamento 101" : "Observações adicionais"}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar Entrada
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default RegistrarVisitanteModal